import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import { Camp, FilterInfo } from '../utils/types';
import type { ScheduleItem } from '../api';
import { FilterOptions } from '../components/FilterBar';


interface CampContextType {
  camps: Camp[] | null;
  setCamps: (camps: Camp[]) => void;
  clearCamps: () => void;
  visibleCamps: Camp[] | null;
  setVisibleCamps: (camps: Camp[] | null) => void;
  filters: FilterOptions;
  setFilters: (filters: FilterOptions) => void;
  participantId: number | null;
  setParticipantId: (id: number | null) => void;
  favorites: Camp[];
  setFavorites: (favorites: Camp[]) => void;
  favoritesUuid: string | null;
  setFavoriteUuid: (uuid: string | null) => void;
  addToFavorites: (camp: Camp) => void;
  removeFromFavorites: (camp: Camp) => void;
  schedules: ScheduleItem[];
  setSchedules: (schedules: ScheduleItem[]) => void;
  sharedSchedules: ScheduleItem[];
  setSharedSchedules: (schedules: ScheduleItem[]) => void;
  currentScheduleUuid: string | null;
  setCurrentScheduleUuid: (id: string | null) => void;
  selectedScheduleIds: string[];
  setSelectedScheduleIds: (selectedScheduleIds: string[]) => void;
  schedule: ScheduleItem | null;
  setSchedule: (schedule: ScheduleItem | null) => void;
  handleScheduleToggle: (scheduleUuid: string) => void;
  handleScheduleDelete: (scheduleUuid: string) => void;
}

const CampContext = createContext<CampContextType | undefined>(undefined);

export const useCampContext = () => {
  const context = useContext(CampContext);
  if (context === undefined) {
    throw new Error('useCampContext must be used within a CampProvider');
  }
  return context;
};

interface CampProviderProps {
  children: ReactNode;
}

export const CampProvider: React.FC<CampProviderProps> = ({ children }) => {
  const [camps, setCamps] = useState<Camp[] | null>(null);
  const [visibleCamps, setVisibleCamps] = useState<Camp[] | null>(null);
  const [participantId, setParticipantId] = useState<number | null>(null);
  const [favorites, setFavorites] = useState<Camp[]>([]);
  const [favoritesUuid, setFavoriteUuid] = useState<string | null>(null);
  const [schedules, setSchedules] = useState<ScheduleItem[]>([]);
  const [sharedSchedules, setSharedSchedules] = useState<ScheduleItem[]>([]);
  const [currentScheduleUuid, setCurrentScheduleUuid] = useState<string | null>(null);
  const [selectedScheduleIds, setSelectedScheduleIds] = useState<string[]>([]);
  const [filters, setFilters] = useState<FilterOptions>({});
  const previousScheduleUuidsRef = useRef<Set<string>>(new Set());

  const addToFavorites = (camp: Camp) => {
    setFavorites([...favorites, camp]);
  };
  const removeFromFavorites = (camp: Camp) => {
    setFavorites(favorites.filter(c => c.id !== camp.id));
  };

  const schedule = schedules.find(s => s.uuid.toString() === currentScheduleUuid) || null;

  const setSchedule = (newSchedule: ScheduleItem | null) => {
    let newSchedules: ScheduleItem[] = [];
    schedules.forEach(s =>  {
      if (newSchedule && s.uuid === newSchedule.uuid) {
        newSchedules.push(newSchedule)
      } else {
        newSchedules.push(s);
      }
    });
    setSchedules(newSchedules);
  }

  const clearCamps = () => {
    setCamps([]);
  };

  const handleScheduleToggle = (scheduleUuid: string) => {
    setSelectedScheduleIds(prev => {
      if (prev.includes(scheduleUuid)) {
        return prev.filter(id => id !== scheduleUuid);
      } else {
        return [...prev, scheduleUuid];
      }
    });
  };

  const handleScheduleDelete = (scheduleUuid: string) => {
    setSelectedScheduleIds(prev => prev.filter(id => id !== scheduleUuid));

    if (currentScheduleUuid === scheduleUuid) {
      const allSchedules = [...schedules, ...sharedSchedules];
      const remaining = allSchedules.filter(s => s.uuid !== scheduleUuid);
      setCurrentScheduleUuid(remaining.length > 0 ? remaining[0].uuid : null);
    }
  };

  return (
    <CampContext.Provider value={{
      camps,
      setCamps,
      clearCamps,
      visibleCamps,
      setVisibleCamps,
      filters,
      setFilters,
      participantId,
      setParticipantId,
      favorites,
      setFavorites,
      favoritesUuid,
      setFavoriteUuid,
      addToFavorites,
      removeFromFavorites,
      schedules,
      setSchedules,
      sharedSchedules,
      setSharedSchedules,
      currentScheduleUuid,
      setCurrentScheduleUuid,
      selectedScheduleIds,
      setSelectedScheduleIds,
      schedule,
      setSchedule,
      handleScheduleToggle,
      handleScheduleDelete
    }}>
      {children}
    </CampContext.Provider>
  );
};
